import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ProtectedCreatorRoute } from "@/components/ProtectedCreatorRoute";
import { SuperAdminRoute } from "@/components/SuperAdminRoute";
import { AuthProvider } from "@/contexts/AuthContext";
import { NotesProvider } from "@/contexts/NotesContext";
import { ChatSettingsProvider } from "@/contexts/ChatSettingsContext";
import { NotificationsProvider } from "@/contexts/NotificationsContext";
import { ModalProvider } from "@/contexts/ModalContext";
import { ThemeProvider } from "next-themes";
import { Suspense, lazy } from "react";
import { HelmetProvider } from "react-helmet-async";
import { SEO } from "@/components/SEO";
import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { AppShell } from "@/components/AppShell";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import Creator from "./pages/Creator";
import Profile from "./pages/Profile";
// Lazy load non-critical routes
const Index = lazy(() => import("./pages/Index"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Notes = lazy(() => import("./pages/Notes"));
const Organizations = lazy(() => import("./pages/Organizations"));
const OrganizationSettings = lazy(() => import("./pages/OrganizationSettings"));
const OrganizationSsoSettings = lazy(() => import("./pages/OrganizationSsoSettings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Discover = lazy(() => import("./pages/Discover"));
const PathwayDetail = lazy(() => import("./pages/PathwayDetail"));
const BlockDetail = lazy(() => import("./pages/BlockDetail"));
const LessonViewer = lazy(() => import("./pages/LessonViewer"));
const LessonEditor = lazy(() => import("./pages/LessonEditor"));
const LessonSearch = lazy(() => import("./pages/LessonSearch"));
const InviteAccept = lazy(() => import("./pages/InviteAccept"));
const TeamInviteAccept = lazy(() => import("./pages/TeamInviteAccept"));
const OrganizationMembers = lazy(() => import("./pages/OrganizationMembers"));
const OrganizationTeams = lazy(() => import("./pages/OrganizationTeams"));
const TeamDetail = lazy(() => import("./pages/TeamDetail"));
const TeamPathways = lazy(() => import("./pages/TeamPathways"));
const TeamDashboard = lazy(() => import("./pages/TeamDashboard"));
const TeamAssignmentRules = lazy(() => import("./pages/TeamAssignmentRules"));
const PlatformAdminPathways = lazy(() => import("./pages/PlatformAdminPathways"));
const PlatformAdminRoles = lazy(() => import("./pages/PlatformAdminRoles"));
const PlatformAdminPathwayExposure = lazy(() => import("./pages/PlatformAdminPathwayExposure"));
const PlatformAdminOrgInvites = lazy(() => import("./pages/PlatformAdminOrgInvites"));
const PlatformAdminFeedback = lazy(() => import("./pages/PlatformAdminFeedback"));
const PlatformAdminInvitations = lazy(() => import("./pages/PlatformAdminInvitations"));
const PlatformAdminOverview = lazy(() => import("./pages/PlatformAdminOverview"));
const PlatformAdminUsers = lazy(() => import("./pages/PlatformAdminUsers"));
const PlatformAdminOrganizations = lazy(() => import("./pages/PlatformAdminOrganizations"));
const PlatformAdminModalTemplates = lazy(() => import("./pages/PlatformAdminModalTemplates"));
const OrgOwnerInviteAccept = lazy(() => import("./pages/OrgOwnerInviteAccept"));
const Assigned = lazy(() => import("./pages/Assigned"));
const MyProgress = lazy(() => import("./pages/MyProgress"));
const Settings = lazy(() => import("./pages/Settings"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const TeamHub = lazy(() => import("./pages/TeamHub"));
const MyLearning = lazy(() => import("./pages/MyLearning"));
const Pathways = lazy(() => import("./pages/Pathways"));
const Explore = lazy(() => import("./pages/Explore"));
const OrgAdminHome = lazy(() => import("./pages/OrgAdminHome"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const ChatHistory = lazy(() => import("./pages/ChatHistory"));
const AiDraftEditor = lazy(() => import("./pages/AiDraftEditor"));
const AiUploadShell = lazy(() => import("./pages/AiUploadShell"));
const PlatformAdminAiUpload = lazy(() => import("./pages/PlatformAdminAiUpload"));
const PlatformAdminContentUpload = lazy(() => import("./pages/PlatformAdminContentUpload"));
const Teams = lazy(() => import("./pages/Teams"));
const SprintLessonViewer = lazy(() => import("./pages/SprintLessonViewer"));
const SprintProgressDashboard = lazy(() => import("./pages/SprintProgressDashboard"));
const MainSearchDemo = lazy(() => import("./pages/MainSearchDemo"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const OrgLogin = lazy(() => import("./pages/OrgLogin"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

const AI_CHAT_ENABLED = import.meta.env.VITE_FEATURE_AI_CHAT === 'true';
const MAIN_SEARCH_ENABLED = import.meta.env.VITE_FEATURE_MAIN_SEARCH === 'true';

// Development-only Sentry test component
const SentryTestPage = () => {
  const handleThrowError = () => {
    throw new Error("Sentry test error from frontend");
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background p-8">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center gap-2">
          <CardTitle>Sentry Test Error</CardTitle>
          <CardDescription>
            Click the button below to trigger a test error (dev only)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={handleThrowError} 
            variant="destructive"
            className="w-full"
            data-testid="button-sentry-test-error"
          >
            Throw Test Error
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

const NotesRoute = () => {
  const { currentOrganization, canManageOrganizations } = useAuth();
  const isOrgLearner = currentOrganization
    ? currentOrganization.role === 'org_user'
    : !canManageOrganizations;
  if (isOrgLearner) return <Navigate to="/dashboard" replace />;
  return <Notes />;
};

const AppContent = () => {
  return (
    <AppErrorBoundary variant="fullpage">
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><SEO noIndex={true} /><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
        <Routes>
        {/* Development-only debug routes */}
        {import.meta.env.DEV && (
          <Route path="/debug/sentry-test" element={<SentryTestPage />} />
        )}
        
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/teams" element={<Teams />} />
        {/* /demo showcased MainSearch, which is out of the pilot scope — retired, not deleted. */}
        <Route path="/demo" element={<Navigate to="/" replace />} />
        {MAIN_SEARCH_ENABLED && <Route path="/main-search-demo" element={<MainSearchDemo />} />}
        <Route path="/auth" element={<Auth />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/org/:slug/login" element={<OrgLogin />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        
        {/* Public learning routes - accessible without authentication */}
        <Route path="/discover" element={<Discover />} />
        <Route path="/creator" element={<Creator />} />
        <Route path="/discover/pathways/:slug" element={<PathwayDetail />} />
        <Route path="/discover/blocks/:slug" element={<BlockDetail />} />
        <Route path="/discover/lessons" element={<LessonSearch />} />
        <Route path="/discover/lessons/:slug" element={<LessonViewer />} />
        <Route path="/sprint/lesson/:lessonId" element={<SprintLessonViewer />} />
        
        {/* Protected creator routes - requires creator authorization */}
        <Route element={<ProtectedCreatorRoute />}>
          <Route path="/creator/lessons/:id" element={<LessonEditor />} />
          <Route path="/creator/ai-upload" element={<AiUploadShell />} />
          <Route path="/creator/ai-upload/:jobId" element={<AiUploadShell />} />
          <Route path="/creator/ai-upload/:jobId/review" element={<AiDraftEditor />} />
        </Route>
        
        {/* Protected routes: AppShell outside ProtectedRoute so sidebar stays visible during auth check */}
        <Route element={<AppShell />}>
          <Route element={<ProtectedRoute />}>
            <Route path="/app" element={<Index />} />
            {AI_CHAT_ENABLED && <Route path="/chat" element={<ChatPage />} />}
            {AI_CHAT_ENABLED && <Route path="/chat/history" element={<ChatHistory />} />}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/team-hub" element={<TeamHub />} />
            <Route path="/team-lead/dashboard" element={<SprintProgressDashboard />} />
            <Route path="/my-learning" element={<MyLearning />} />
            <Route path="/pathways" element={<Pathways />} />
            <Route path="/explore" element={<Explore />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/notes" element={<NotesRoute />} />
            <Route path="/assigned" element={<Assigned />} />
            <Route path="/my-progress" element={<MyProgress />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/organizations" element={<Organizations />} />
            <Route path="/organizations/:id" element={<OrgAdminHome />} />
            <Route path="/organizations/:id/settings" element={<OrganizationSettings />} />
            <Route path="/organizations/:id/settings/sso" element={<OrganizationSsoSettings />} />
            <Route path="/organizations/:id/members" element={<OrganizationMembers />} />
            <Route path="/organizations/:id/teams" element={<OrganizationTeams />} />
            <Route path="/organizations/:id/teams/rules" element={<TeamAssignmentRules />} />
            <Route path="/organizations/:orgId/teams/:teamId" element={<TeamDetail />} />
            <Route path="/organizations/:orgId/teams/:teamId/pathways" element={<TeamPathways />} />
            <Route path="/organizations/:orgId/teams/:teamId/dashboard" element={<TeamDashboard />} />
            <Route path="/invite/:token" element={<InviteAccept />} />
            <Route path="/invite/team/:token" element={<TeamInviteAccept />} />
            <Route path="/invite/org-owner/:token" element={<OrgOwnerInviteAccept />} />
          </Route>
          
          {/* Super Admin routes share the same AppShell */}
          <Route element={<SuperAdminRoute />}>
            <Route path="/admin/overview" element={<PlatformAdminOverview />} />
            <Route path="/admin/pathways" element={<PlatformAdminPathways />} />
            <Route path="/admin/roles" element={<PlatformAdminRoles />} />
            <Route path="/admin/pathway-exposure" element={<PlatformAdminPathwayExposure />} />
            <Route path="/admin/org-invites" element={<PlatformAdminOrgInvites />} />
            <Route path="/admin/feedback" element={<PlatformAdminFeedback />} />
            <Route path="/admin/access-keys" element={<PlatformAdminInvitations />} />
            <Route path="/admin/users" element={<PlatformAdminUsers />} />
            <Route path="/admin/organizations" element={<PlatformAdminOrganizations />} />
            <Route path="/admin/modal-templates" element={<PlatformAdminModalTemplates />} />
            <Route path="/admin/ai-upload" element={<PlatformAdminAiUpload />} />
            <Route path="/admin/upload" element={<PlatformAdminContentUpload />} />
          </Route>
        </Route>
        
        {/* Catch-all for 404 */}
        <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AppErrorBoundary>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <Sentry.ErrorBoundary fallback={({resetError}) => (
        <div className="flex flex-col items-center justify-center min-h-screen bg-background p-8" data-testid="error-boundary-root">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center gap-2">
              <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <CardTitle>Something went wrong</CardTitle>
              <CardDescription>
                An unexpected error occurred. Please try refreshing the page.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <Button onClick={() => window.location.reload()} data-testid="button-error-refresh-root">
                <RefreshCw className="w-4 h-4" />
                Refresh Page
              </Button>
              <Button variant="outline" onClick={() => { window.location.href = "/"; }} data-testid="button-error-go-home-root">
                <Home className="w-4 h-4" />
                Go to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      )}>
        <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <AuthProvider>
                <ChatSettingsProvider>
                  <NotificationsProvider>
                    <NotesProvider>
                      <ModalProvider>
                        <AppContent />
                      </ModalProvider>
                    </NotesProvider>
                  </NotificationsProvider>
                </ChatSettingsProvider>
              </AuthProvider>
            </BrowserRouter>
          </TooltipProvider>
        </ThemeProvider>
      </Sentry.ErrorBoundary>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
